import { DOCUMENT, Injectable, inject } from '@angular/core';
import { WA_WINDOW } from '@ng-web-apis/common';

interface SubjectOptions {
  commonName: string;
  email: string;
  organization?: string;
  organizationalUnit?: string;
  locality?: string;
  state?: string;
  country?: string;
}

interface AndroidKeystoreOptions extends SubjectOptions {
  password: string;
  keyAlias: string;
  validityYears: number;
}

interface IosP12Options {
  privateKey: import('node-forge').pki.rsa.PrivateKey;
  cerData: ArrayBuffer;
  password: string;
}

@Injectable()
export class CertificateService {
  private readonly document = inject(DOCUMENT);
  private readonly window = inject(WA_WINDOW);

  public async generateAndroidKeystore(
    options: AndroidKeystoreOptions,
  ): Promise<ArrayBuffer> {
    const forge = await CertificateService.importForge();

    const keys = await this.generateKeyPair(forge);

    const certificate = forge.pki.createCertificate();
    certificate.publicKey = keys.publicKey;
    certificate.serialNumber = String(Date.now());
    certificate.validity.notBefore = new Date();
    const notAfter = new Date();
    notAfter.setFullYear(notAfter.getFullYear() + options.validityYears);
    certificate.validity.notAfter = notAfter;

    const attributes = CertificateService.buildSubjectAttributes(options);
    certificate.setSubject(attributes);
    certificate.setIssuer(attributes);

    certificate.sign(keys.privateKey, forge.md.sha256.create());

    const p12Asn1 = forge.pkcs12.toPkcs12Asn1(
      keys.privateKey,
      certificate,
      options.password,
      { algorithm: '3des', friendlyName: options.keyAlias },
    );

    return CertificateService.asn1ToArrayBuffer(forge, p12Asn1);
  }

  public async generateIosCsr(subject: SubjectOptions): Promise<{
    csrPem: string;
    privateKey: import('node-forge').pki.rsa.PrivateKey;
  }> {
    const forge = await CertificateService.importForge();

    const keys = await this.generateKeyPair(forge);

    const csr = forge.pki.createCertificationRequest();
    csr.publicKey = keys.publicKey;
    csr.setSubject(CertificateService.buildSubjectAttributes(subject));
    csr.sign(keys.privateKey, forge.md.sha256.create());

    const csrPem = forge.pki.certificationRequestToPem(csr);

    return { csrPem, privateKey: keys.privateKey };
  }

  public async validateCertificateFile(cerData: ArrayBuffer): Promise<void> {
    const forge = await CertificateService.importForge();
    const derString = CertificateService.arrayBufferToBinaryString(cerData);
    const asn1 = forge.asn1.fromDer(derString);
    forge.pki.certificateFromAsn1(asn1);
  }

  public async generateIosP12(options: IosP12Options): Promise<ArrayBuffer> {
    const forge = await CertificateService.importForge();

    const derString = CertificateService.arrayBufferToBinaryString(
      options.cerData,
    );
    let certificate: import('node-forge').pki.Certificate;
    try {
      const asn1 = forge.asn1.fromDer(derString);
      certificate = forge.pki.certificateFromAsn1(asn1);
    } catch {
      throw new Error(
        'The uploaded file could not be read. Please make sure it is a valid .cer certificate file.',
      );
    }

    const p12Asn1 = forge.pkcs12.toPkcs12Asn1(
      options.privateKey,
      certificate,
      options.password,
      { algorithm: '3des', friendlyName: 'key' },
    );

    return CertificateService.asn1ToArrayBuffer(forge, p12Asn1);
  }

  public downloadFile(
    data: ArrayBuffer | string,
    filename: string,
    mimeType: string,
  ): void {
    const blob = new Blob([data], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const a = this.document.createElement('a');
    a.href = url;
    a.download = filename;
    this.document.body.append(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }

  // Uses Web Crypto API instead of forge.pki.rsa.generateKeyPair() to avoid
  // blocking the main thread during RSA prime generation.
  private async generateKeyPair(
    forge: typeof import('node-forge'),
  ): Promise<import('node-forge').pki.rsa.KeyPair> {
    const subtle = this.window.crypto?.subtle;
    if (!subtle) {
      throw new Error(
        'Web Crypto API is not available. Please use HTTPS to access this feature.',
      );
    }
    const cryptoKeyPair = await subtle.generateKey(
      {
        name: 'RSASSA-PKCS1-v1_5',
        modulusLength: 2048,
        publicExponent: new Uint8Array([1, 0, 1]),
        hash: 'SHA-256',
      },
      true,
      ['sign', 'verify'],
    );
    const [privatePkcs8, publicSpki] = await Promise.all([
      subtle.exportKey('pkcs8', cryptoKeyPair.privateKey),
      subtle.exportKey('spki', cryptoKeyPair.publicKey),
    ]);
    const privateKey = forge.pki.privateKeyFromAsn1(
      forge.asn1.fromDer(
        CertificateService.arrayBufferToBinaryString(privatePkcs8),
      ),
    );
    const publicKey = forge.pki.publicKeyFromAsn1(
      forge.asn1.fromDer(
        CertificateService.arrayBufferToBinaryString(publicSpki),
      ),
    );
    return { privateKey, publicKey };
  }

  private static async importForge(): Promise<typeof import('node-forge')> {
    const module = await import('node-forge');
    return (module as any).default ?? module;
  }

  private static buildSubjectAttributes(
    subject: SubjectOptions,
  ): import('node-forge').pki.CertificateField[] {
    const attributes: import('node-forge').pki.CertificateField[] = [
      { shortName: 'CN', value: subject.commonName },
      { shortName: 'E', value: subject.email },
    ];
    if (subject.organization) {
      attributes.push({ shortName: 'O', value: subject.organization });
    }
    if (subject.organizationalUnit) {
      attributes.push({ shortName: 'OU', value: subject.organizationalUnit });
    }
    if (subject.locality) {
      attributes.push({ shortName: 'L', value: subject.locality });
    }
    if (subject.state) {
      attributes.push({ shortName: 'ST', value: subject.state });
    }
    if (subject.country) {
      attributes.push({ shortName: 'C', value: subject.country });
    }
    return attributes;
  }

  private static asn1ToArrayBuffer(
    forge: typeof import('node-forge'),
    asn1: import('node-forge').asn1.Asn1,
  ): ArrayBuffer {
    const derBytes = forge.asn1.toDer(asn1).getBytes();
    return CertificateService.binaryStringToArrayBuffer(derBytes);
  }

  private static binaryStringToArrayBuffer(binary: string): ArrayBuffer {
    const buffer = new ArrayBuffer(binary.length);
    const view = new Uint8Array(buffer);
    for (let index = 0; index < binary.length; index++) {
      view[index] = binary.codePointAt(index) ?? 0;
    }
    return buffer;
  }

  private static arrayBufferToBinaryString(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (const byte of bytes) {
      binary += String.fromCodePoint(byte);
    }
    return binary;
  }
}
